import { useScheduleChangeLog, type ScheduleChangeLog } from '@/hooks/useScheduleChangeLog';
import { formatDateFull } from '@/lib/scheduleUtils';

interface Props {
  projectId: string;
  companyName?: string | null;
}

function isRelevantToSubcontractor(log: ScheduleChangeLog, companyName?: string | null): boolean {
  if (!companyName) return false;
  const name = companyName.toLowerCase();
  const title = (log.schedule_item_title || '').toLowerCase();
  return (
    title.includes(name) ||
    log.change_type === 'item_delayed' ||
    log.change_type === 'item_completed' ||
    (log.change_type === 'linked_items_recalculated' && log.affected_item_count > 0)
  );
}

export default function SubcontractorScheduleUpdates({ projectId, companyName }: Props) {
  const { logs, loading } = useScheduleChangeLog(projectId);

  const relevant = logs.filter((log) => isRelevantToSubcontractor(log, companyName));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-lg"></i>
      </div>
    );
  }

  if (relevant.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <i className="ri-time-line text-[#6B7280]"></i>
          <span className="text-sm font-bold text-[#1A1A1A]">Schedule Updates</span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F9FAFB] text-[#6B7280]">
            {relevant.length}
          </span>
        </div>
      </div>
      <div className="px-4 py-3 space-y-2 max-h-64 overflow-y-auto">
        {relevant.slice(0, 10).map((log) => (
          <div key={log.id} className="bg-[#F9FAFB] rounded-xl p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#1A1A1A]">
                  {log.schedule_item_title || 'Project-wide change'}
                </p>
                <p className="text-[10px] text-[#9CA3AF] capitalize mt-0.5">
                  {log.change_type.replace(/_/g, ' ')}
                </p>
                {log.old_start_date && log.new_start_date && (
                  <p className="text-[10px] text-[#6B7280] mt-1">
                    {formatDateFull(log.old_start_date)} → {formatDateFull(log.new_start_date)}
                    {log.old_end_date && log.new_end_date ? ` · End: ${formatDateFull(log.old_end_date)} → ${formatDateFull(log.new_end_date)}` : ''}
                  </p>
                )}
                {log.change_reason && (
                  <p className="text-[10px] text-[#6B7280] mt-1">{log.change_reason}</p>
                )}
              </div>
              <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap flex-shrink-0">
                {new Date(log.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}